import type { Spec as VegaSpec } from 'vega';
import type { TopLevelSpec } from 'vega-lite';

const NESTED_SPEC_KEYS = ['layer', 'concat', 'hconcat', 'vconcat', 'spec'];
const VEGA_LITE_SPEC_KEYS = [
  'mark',
  'encoding',
  'layer',
  'hconcat',
  'vconcat',
  'facet',
  'repeat',
];

export const isVegaLiteSpec = (spec: Record<string, unknown>): boolean => {
  if (spec.$schema) {
    return (
      typeof spec.$schema === 'string' && spec.$schema.includes('vega-lite')
    );
  }

  return VEGA_LITE_SPEC_KEYS.some(key => key in spec);
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const getChildSpecs = (
  spec: Record<string, unknown>,
): Record<string, unknown>[] =>
  NESTED_SPEC_KEYS.flatMap(key => {
    const child = spec[key];
    return (Array.isArray(child) ? child : [child]).filter(isRecord);
  });

const getMarkType = (mark: unknown): string | undefined => {
  if (typeof mark === 'string') return mark;
  if (!isRecord(mark)) return undefined;

  const markType = mark.type;
  return typeof markType === 'string' ? markType : undefined;
};

// Recursively inject compact number formatting ("~s": SI prefix, e.g. 14M, 50K)
// on any quantitative axis encoding that does not already have an explicit format.
// Only touches `type: "quantitative"` channels so temporal/ordinal axes are unaffected.
export function injectCompactAxisFormat(spec: Record<string, unknown>): void {
  if (spec.encoding && typeof spec.encoding === 'object') {
    const encoding = spec.encoding as Record<string, unknown>;
    for (const channel of ['x', 'y', 'x2', 'y2']) {
      const enc = encoding[channel] as Record<string, unknown> | undefined;
      if (enc && enc.type === 'quantitative') {
        const axis = (enc.axis as Record<string, unknown> | null) ?? {};
        if (!axis.format) {
          enc.axis = { ...axis, format: '~s' };
        }
      }
    }
  }

  for (const key of NESTED_SPEC_KEYS) {
    const child = spec[key];
    if (Array.isArray(child)) {
      for (const sub of child) {
        if (typeof sub === 'object' && sub !== null) {
          injectCompactAxisFormat(sub as Record<string, unknown>);
        }
      }
    } else if (typeof child === 'object' && child !== null) {
      injectCompactAxisFormat(child as Record<string, unknown>);
    }
  }
}

const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;
const DATE_FORMAT_DIRECTIVE = /%(?:a|A|b|B|d|e|g|G|j|m|q|u|U|V|w|W|x|y|Y)/;
const TIME_FORMAT_DIRECTIVE = /%(?:c|f|H|I|L|M|p|Q|s|S|X)/;
const DATE_ONLY_VALUE = /^\d{4}-\d{2}-\d{2}$/;
const TEMPORAL_TICK_PROPERTIES = ['tickCount', 'tickMinStep', 'values'];

const hasDateOnlyFormat = (format: unknown): format is string =>
  typeof format === 'string' &&
  DATE_FORMAT_DIRECTIVE.test(format) &&
  !TIME_FORMAT_DIRECTIVE.test(format);

const getInlineDataRows = (
  spec: Record<string, unknown>,
  inheritedRows: Record<string, unknown>[] = [],
  datasets: Record<string, unknown> | undefined = undefined,
): Record<string, unknown>[] => {
  const data = spec.data;
  if (data === undefined) return inheritedRows;

  if (!isRecord(data)) return [];

  const dataDefinition = data;
  const values =
    typeof dataDefinition.name === 'string' && datasets !== undefined
      ? datasets[dataDefinition.name]
      : dataDefinition.values;
  if (!Array.isArray(values)) return [];

  if (!values.every(isRecord)) return [];

  return values;
};

const CONFIDENCE_PRESENTATION_CHANNELS = ['color', 'fill', 'stroke'];
const LEGACY_THEMEABLE_SERIES_MARK_TYPES = new Set([
  'arc',
  'area',
  'bar',
  'boxplot',
  'circle',
  'errorband',
  'errorbar',
  'line',
  'point',
  'rect',
  'square',
  'tick',
  'trail',
]);
const HOLLOW_MARK_TYPES = new Set(['circle', 'point', 'square']);
const NEUTRAL_COLOR_NAMES = new Set(['black', 'gray', 'grey', 'white']);
const NON_PAINT_COLOR_NAMES = new Set(['none', 'transparent']);
const POSITION_CHANNELS = ['x', 'y'];
export const CONFIDENCE_OVERFLOW_CATEGORICAL_SCHEME = 'tableau20';
const CONFIDENCE_LINE_DASH_PATTERNS = [
  [],
  [8, 4],
  [2, 3],
  [10, 3, 2, 3],
  [1, 4],
  [12, 3, 3, 3],
  [6, 2, 1, 2],
  [5, 5],
];

const hasExplicitStrokeDash = (mark: unknown): boolean =>
  typeof mark === 'object' &&
  mark !== null &&
  !Array.isArray(mark) &&
  'strokeDash' in mark;

const getStrokeLegend = (legend: unknown): Record<string, unknown> | null =>
  legend === null
    ? null
    : {
        symbolType: 'stroke',
        ...(typeof legend === 'object' && !Array.isArray(legend) ? legend : {}),
      };

type CategoricalEncodingType = 'nominal' | 'ordinal';
const CATEGORICAL_ENCODING_TYPES: readonly string[] = ['nominal', 'ordinal'];

const isCategoricalEncoding = (
  encodedField: Record<string, unknown>,
): encodedField is Record<string, unknown> & {
  type: CategoricalEncodingType;
} =>
  typeof encodedField.type === 'string' &&
  CATEGORICAL_ENCODING_TYPES.includes(encodedField.type);

type CategoricalPosition = {
  field: string;
  type: CategoricalEncodingType;
};

const getCategoricalPosition = (
  encoding: Record<string, unknown>,
): CategoricalPosition | undefined => {
  for (const channel of POSITION_CHANNELS) {
    const encodedField = encoding[channel];
    if (typeof encodedField !== 'object' || encodedField === null) continue;

    const fieldDefinition = encodedField as Record<string, unknown>;
    if (
      typeof fieldDefinition.field === 'string' &&
      isCategoricalEncoding(fieldDefinition)
    ) {
      return {
        field: fieldDefinition.field,
        type: fieldDefinition.type,
      };
    }
  }

  return undefined;
};

const getDistinctValueKey = (value: unknown): unknown => {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'object' && value !== null) {
    return JSON.stringify(value);
  }
  return value;
};

const getDistinctValueCount = (
  rows: Record<string, unknown>[],
  field: string,
): number =>
  new Set(
    rows
      .map(row => row[field])
      .map(getDistinctValueKey)
      .filter(value => value !== null && value !== undefined),
  ).size;

type ConfidenceTransformView = {
  initialize: () => ConfidenceTransformView;
  run: () => ConfidenceTransformView;
  data: (name: string) => Record<string, unknown>[];
  finalize: () => void;
};

export type ConfidenceSeriesTransformRuntime = {
  compile: (spec: TopLevelSpec) => VegaSpec;
  createView: (spec: VegaSpec) => ConfidenceTransformView;
};

type ConfidenceSeriesPalettes = {
  categorical: readonly string[];
  overflow: readonly string[];
  sequential: readonly string[];
};

type RepeatValues = Record<string, readonly unknown[]>;

const applyConfidenceSeriesTransforms = (
  rows: Record<string, unknown>[],
  transform: unknown,
  runtime: ConfidenceSeriesTransformRuntime | undefined,
): Record<string, unknown>[] => {
  if (!Array.isArray(transform) || runtime === undefined) return rows;

  let view: ConfidenceTransformView | undefined;
  try {
    const transformedSpec = {
      data: { values: rows.map(row => ({ ...row })) },
      transform,
      mark: 'point',
    } as TopLevelSpec;
    const compiledSpec = runtime.compile(transformedSpec);
    const firstMark = compiledSpec.marks?.[0];
    const dataName = firstMark?.from?.data;
    if (typeof dataName !== 'string') return rows;

    view = runtime.createView(compiledSpec);
    view.initialize().run();
    return view.data(dataName).map(row => ({ ...row }));
  } catch {
    return rows;
  } finally {
    view?.finalize();
  }
};

const getRepeatValues = (
  repeat: unknown,
  inheritedRepeatValues: RepeatValues,
): RepeatValues => {
  if (Array.isArray(repeat)) {
    return { ...inheritedRepeatValues, repeat };
  }
  if (typeof repeat !== 'object' || repeat === null) {
    return inheritedRepeatValues;
  }

  return Object.entries(repeat).reduce<RepeatValues>(
    (repeatValues, [key, values]) =>
      Array.isArray(values) ? { ...repeatValues, [key]: values } : repeatValues,
    inheritedRepeatValues,
  );
};

const hasSeriesPalette = (encoding: Record<string, unknown>): boolean => {
  const scale = encoding.scale;
  return (
    isCategoricalEncoding(encoding) &&
    !('condition' in encoding) &&
    typeof scale === 'object' &&
    scale !== null &&
    ('range' in scale || 'scheme' in scale)
  );
};

const getCategoricalEncodingValues = (
  encoding: Record<string, unknown>,
  rows: Record<string, unknown>[],
  repeatValues: RepeatValues,
): readonly unknown[] | undefined => {
  if (!isCategoricalEncoding(encoding)) return undefined;
  if ('datum' in encoding) return [encoding.datum];
  const scale = encoding.scale as Record<string, unknown> | undefined;
  if (Array.isArray(scale?.domain)) return scale.domain;

  const field = encoding.field;
  if (typeof field === 'string') {
    const hasValues = hasSeriesPalette(encoding)
      ? rows.length > 0 && rows.every(row => field in row)
      : rows.some(row => field in row);
    return hasValues ? rows.map(row => row[field]) : undefined;
  }
  if (typeof field !== 'object' || field === null || Array.isArray(field)) {
    return undefined;
  }

  const repeat = (field as Record<string, unknown>).repeat;
  return typeof repeat === 'string' ? repeatValues[repeat] : undefined;
};

const hasAuthoredScale = (encoding: Record<string, unknown>): boolean =>
  'scale' in encoding && !hasSeriesPalette(encoding);

const hasExplicitMarkPresentation = (mark: unknown): boolean =>
  isRecord(mark) &&
  CONFIDENCE_PRESENTATION_CHANNELS.some(channel => mark[channel] !== undefined);

const getEncoding = (
  spec: Record<string, unknown>,
): Record<string, unknown> | undefined =>
  isRecord(spec.encoding) ? spec.encoding : undefined;

const isThemeableSeries = (
  spec: Record<string, unknown>,
  encoding: Record<string, unknown> = getEncoding(spec) ?? {},
): boolean => {
  const markType = getMarkType(spec.mark);
  return (
    (markType !== undefined &&
      LEGACY_THEMEABLE_SERIES_MARK_TYPES.has(markType)) ||
    (markType === 'rule' &&
      POSITION_CHANNELS.some(channel => {
        const start = encoding[channel];
        const end = encoding[`${channel}2`];
        return (
          isRecord(start) && 'field' in start && isRecord(end) && 'field' in end
        );
      }))
  );
};

const isAchromaticHexColor = (color: string): boolean => {
  const hex = color.startsWith('#') ? color.slice(1) : '';
  if (hex.length === 3 || hex.length === 4) {
    return hex[0] === hex[1] && hex[1] === hex[2];
  }
  if (hex.length === 6 || hex.length === 8) {
    return (
      hex.slice(0, 2) === hex.slice(2, 4) && hex.slice(2, 4) === hex.slice(4, 6)
    );
  }
  return false;
};

const isNeutralColor = (color: string): boolean => {
  const normalizedColor = color.trim().toLowerCase();
  return (
    NEUTRAL_COLOR_NAMES.has(normalizedColor) ||
    normalizedColor.includes('gray') ||
    normalizedColor.includes('grey') ||
    isAchromaticHexColor(normalizedColor)
  );
};

const hasMarkOrEncoding = (
  spec: Record<string, unknown>,
  channel: string,
): boolean =>
  (isRecord(spec.mark) && spec.mark[channel] !== undefined) ||
  getEncoding(spec)?.[channel] !== undefined;

const preservesLegacyPresentation = (
  spec: Record<string, unknown>,
  markType: string | undefined,
  channel: string,
  color: string,
): boolean => {
  if (NON_PAINT_COLOR_NAMES.has(color.trim().toLowerCase())) return true;

  return (
    (channel === 'fill' &&
      markType !== undefined &&
      HOLLOW_MARK_TYPES.has(markType) &&
      hasMarkOrEncoding(spec, 'stroke') &&
      isNeutralColor(color)) ||
    (['color', 'stroke'].includes(channel) &&
      markType === 'line' &&
      hasMarkOrEncoding(spec, 'strokeDash') &&
      isNeutralColor(color))
  );
};

const normalizeSeriesMarkConfig = (
  config: Record<string, unknown>,
  markType: string,
  primaryColor: string,
): Record<string, unknown> => {
  const defaults = {
    ...(isRecord(config.mark) ? config.mark : {}),
    ...(isRecord(config[markType]) ? config[markType] : {}),
  };
  const result = { ...(isRecord(config[markType]) ? config[markType] : {}) };
  for (const channel of CONFIDENCE_PRESENTATION_CHANNELS) {
    const color = defaults[channel];
    if (
      typeof color === 'string' &&
      !preservesLegacyPresentation(
        { mark: { ...defaults, type: markType } },
        markType,
        channel,
        color,
      )
    ) {
      result[channel] = primaryColor;
    } else if (color !== undefined) {
      result[channel] = color;
    }
  }
  return result;
};

const normalizeCategoricalConfig = (
  spec: Record<string, unknown>,
  palettes: ConfidenceSeriesPalettes,
): Record<string, unknown> => {
  if (!isRecord(spec.config)) return {};
  const config = spec.config;
  if (isRecord(config.range)) {
    for (const key of ['category', 'ordinal']) {
      if (key in config.range) config.range[key] = [...palettes.categorical];
    }
  }
  return config;
};

type SeriesColorTarget = {
  owner: Record<string, unknown>;
  key: string;
  color: string;
};

const getSeriesColorTargets = (
  spec: Record<string, unknown>,
): SeriesColorTarget[] => {
  const markType = getMarkType(spec.mark);
  if (!isThemeableSeries(spec)) return [];
  const targets: SeriesColorTarget[] = [];
  const mark = isRecord(spec.mark) ? spec.mark : {};
  const encoding = getEncoding(spec) ?? {};
  for (const channel of CONFIDENCE_PRESENTATION_CHANNELS) {
    const color = mark[channel];
    if (
      typeof color === 'string' &&
      !preservesLegacyPresentation(spec, markType, channel, color)
    ) {
      targets.push({ owner: mark, key: channel, color });
    }
  }
  for (const channel of CONFIDENCE_PRESENTATION_CHANNELS) {
    const definition = encoding[channel];
    if (!isRecord(definition) || 'condition' in definition) continue;
    const color = definition.value;
    if (
      typeof color === 'string' &&
      !preservesLegacyPresentation(spec, markType, channel, color)
    ) {
      targets.push({ owner: definition, key: 'value', color });
    }
  }
  return targets;
};

const prepareSeriesPresentation = (
  spec: Record<string, unknown>,
  config: Record<string, unknown>,
  inheritedEncoding: Record<string, unknown> = {},
): void => {
  const localEncoding = getEncoding(spec) ?? {};
  const encoding = { ...inheritedEncoding, ...localEncoding };
  if (isThemeableSeries(spec, encoding)) {
    const markType = getMarkType(spec.mark);
    if (markType === 'rule') {
      const mark = isRecord(spec.mark) ? spec.mark : { type: 'rule' };
      const hasSeriesColor = ['color', 'stroke'].some(
        channel =>
          mark[channel] !== undefined || encoding[channel] !== undefined,
      );
      const defaults = Object.fromEntries(
        (hasSeriesColor ? [] : CONFIDENCE_PRESENTATION_CHANNELS).flatMap(
          channel => {
            const color =
              (isRecord(config.rule) ? config.rule[channel] : undefined) ??
              (isRecord(config.mark) ? config.mark[channel] : undefined);
            return color === undefined ? [] : [[channel, color]];
          },
        ),
      );
      for (const channel of ['x', 'y', 'x2', 'y2']) {
        if (encoding[channel] !== undefined) {
          localEncoding[channel] = encoding[channel];
        }
      }
      spec.encoding = localEncoding;
      spec.mark = { ...defaults, ...mark };
    }
    for (const channel of CONFIDENCE_PRESENTATION_CHANNELS) {
      if (localEncoding[channel] !== undefined) continue;
      const definition = inheritedEncoding[channel];
      if (
        !isRecord(definition) ||
        typeof definition.value !== 'string' ||
        'condition' in definition
      )
        continue;
      localEncoding[channel] = { ...definition };
      spec.encoding = localEncoding;
    }
  }
  const independentScales =
    isRecord(spec.resolve) && isRecord(spec.resolve.scale)
      ? spec.resolve.scale
      : {};
  for (const child of getChildSpecs(spec)) {
    for (const channel of CONFIDENCE_PRESENTATION_CHANNELS) {
      if (
        independentScales[channel] !== 'independent' ||
        !isRecord(encoding[channel])
      )
        continue;
      const childEncoding = getEncoding(child) ?? {};
      if (childEncoding[channel] !== undefined) continue;
      childEncoding[channel] = { ...encoding[channel] };
      child.encoding = childEncoding;
    }
    prepareSeriesPresentation(child, config, encoding);
  }
};

type SeriesColorUsage = {
  literals: Set<string>;
  configuredDefaults: Map<string, string | undefined>;
  occupied: Set<string>;
};

const collectSeriesColorUsage = (
  spec: Record<string, unknown>,
  usage: SeriesColorUsage,
  config: Record<string, unknown>,
  primaryColor: string,
  inheritedEncoding: Record<string, unknown> = {},
): void => {
  const encoding = { ...inheritedEncoding, ...getEncoding(spec) };
  const identity = getSeriesColorTargets(spec)[0]?.color;
  if (identity !== undefined) usage.literals.add(identity);
  const markType = getMarkType(spec.mark);
  if (markType !== undefined && isThemeableSeries(spec, encoding)) {
    const defaults = {
      ...(isRecord(config.mark) ? config.mark : {}),
      ...(isRecord(config[markType]) ? config[markType] : {}),
      type: markType,
    };
    const defaultIdentity = getSeriesColorTargets({ mark: defaults })[0]?.color;
    if (
      markType !== 'rule' &&
      (isRecord(config.mark) || isRecord(config[markType]))
    ) {
      usage.configuredDefaults.set(markType, defaultIdentity);
    }
    if (
      !hasExplicitMarkPresentation(spec.mark) &&
      !CONFIDENCE_PRESENTATION_CHANNELS.some(
        channel => encoding[channel] !== undefined,
      )
    ) {
      if (defaultIdentity !== undefined) usage.literals.add(defaultIdentity);
      else if (!hasExplicitMarkPresentation(defaults))
        usage.occupied.add(primaryColor);
    }
  }
  for (const child of getChildSpecs(spec)) {
    collectSeriesColorUsage(child, usage, config, primaryColor, encoding);
  }
};

const getConfidenceSeriesScale = (
  distinctValueCount: number,
  encodingType: CategoricalEncodingType,
  palettes: ConfidenceSeriesPalettes,
): Record<string, unknown> | undefined => {
  if (distinctValueCount < 1) return undefined;
  if (
    encodingType === 'ordinal' &&
    distinctValueCount <= palettes.sequential.length
  ) {
    return { range: [...palettes.sequential] };
  }

  return distinctValueCount <= palettes.categorical.length
    ? { range: [...palettes.categorical] }
    : { scheme: CONFIDENCE_OVERFLOW_CATEGORICAL_SCHEME };
};

const applyThemeToLegacySeriesColors = (
  spec: Record<string, unknown>,
  colorMapping: ReadonlyMap<string, string>,
): void => {
  const targets = getSeriesColorTargets(spec);
  const identity = targets[0]?.color;
  const color = identity === undefined ? undefined : colorMapping.get(identity);
  if (color !== undefined) {
    for (const target of targets) target.owner[target.key] = color;
  }
  for (const child of getChildSpecs(spec))
    applyThemeToLegacySeriesColors(child, colorMapping);
};

const setSeriesScale = (
  encoding: Record<string, unknown>,
  scale: Record<string, unknown>,
): void => {
  const {
    range: _range,
    scheme: _scheme,
    ...options
  } = (encoding.scale ?? {}) as Record<string, unknown>;
  encoding.scale = { ...options, ...scale };
};

const rememberSeriesColors = (
  encoding: Record<string, unknown>,
  palettes: ConfidenceSeriesPalettes,
  occupiedColors: Set<string>,
  distinctValueCount?: number,
): void => {
  const scale = encoding.scale;
  if (scale === null) return;
  let colors = palettes.categorical;
  if (isRecord(scale)) {
    if (Array.isArray(scale.range))
      colors = scale.range.filter(
        (color): color is string => typeof color === 'string',
      );
    else if (scale.scheme === CONFIDENCE_OVERFLOW_CATEGORICAL_SCHEME)
      colors = palettes.overflow;
    if (scale.reverse === true) colors = [...colors].reverse();
  }
  for (const color of colors.slice(0, distinctValueCount))
    occupiedColors.add(color);
};

const removeRedundantBarValueColor = (
  localEncoding: Record<string, unknown>,
  encoding: Record<string, unknown>,
): void => {
  const quantitativePositionFields = new Set(
    POSITION_CHANNELS.flatMap(channel => {
      const encodedField = encoding[channel];
      if (typeof encodedField !== 'object' || encodedField === null) return [];

      const fieldDefinition = encodedField as Record<string, unknown>;
      return fieldDefinition.type === 'quantitative' &&
        typeof fieldDefinition.field === 'string'
        ? [fieldDefinition.field]
        : [];
    }),
  );

  for (const channel of CONFIDENCE_PRESENTATION_CHANNELS) {
    const encodedField = encoding[channel];
    if (typeof encodedField !== 'object' || encodedField === null) continue;

    const fieldDefinition = encodedField as Record<string, unknown>;
    if (
      fieldDefinition.type === 'quantitative' &&
      typeof fieldDefinition.field === 'string' &&
      quantitativePositionFields.has(fieldDefinition.field)
    ) {
      Reflect.deleteProperty(localEncoding, channel);
      Reflect.deleteProperty(encoding, channel);
    }
  }
};

const normalizeCategoricalBarValueScale = (
  encoding: Record<string, unknown>,
): void => {
  if (encoding.x2 !== undefined || encoding.y2 !== undefined) return;

  for (const [categoryChannel, valueChannel] of [
    ['x', 'y'],
    ['y', 'x'],
  ]) {
    const categoryEncoding = encoding[categoryChannel];
    const valueEncoding = encoding[valueChannel];
    if (
      typeof categoryEncoding !== 'object' ||
      categoryEncoding === null ||
      typeof valueEncoding !== 'object' ||
      valueEncoding === null
    ) {
      continue;
    }

    const categoryFieldDefinition = categoryEncoding as Record<string, unknown>;
    const valueFieldDefinition = valueEncoding as Record<string, unknown>;
    if (
      !isCategoricalEncoding(categoryFieldDefinition) ||
      typeof categoryFieldDefinition.field !== 'string' ||
      valueFieldDefinition.type !== 'quantitative' ||
      valueFieldDefinition.scale === null
    ) {
      continue;
    }

    const scale = valueFieldDefinition.scale;
    valueFieldDefinition.scale = {
      ...(typeof scale === 'object' && !Array.isArray(scale) ? scale : {}),
      zero: true,
    };
    return;
  }
};

const getDatasets = (
  spec: Record<string, unknown>,
  inheritedDatasets: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined =>
  isRecord(spec.datasets) ? spec.datasets : inheritedDatasets;

const getTransformedRows = (
  spec: Record<string, unknown>,
  inheritedRows: Record<string, unknown>[],
  datasets: Record<string, unknown> | undefined,
  transformRuntime: ConfidenceSeriesTransformRuntime | undefined,
): Record<string, unknown>[] =>
  applyConfidenceSeriesTransforms(
    getInlineDataRows(spec, inheritedRows, datasets),
    spec.transform,
    transformRuntime,
  );

const injectLineDashStyle = (
  spec: Record<string, unknown>,
  localEncoding: Record<string, unknown>,
  encoding: Record<string, unknown>,
  categoricalEncoding: Record<string, unknown>,
  distinctValueCount: number,
  palettes: ConfidenceSeriesPalettes,
): void => {
  const seriesField = categoricalEncoding.field;
  if (
    getMarkType(spec.mark) !== 'line' ||
    seriesField === undefined ||
    distinctValueCount <= palettes.sequential.length ||
    distinctValueCount > CONFIDENCE_LINE_DASH_PATTERNS.length
  ) {
    return;
  }

  if (
    localEncoding.strokeDash === undefined &&
    encoding.strokeDash === undefined &&
    !hasExplicitStrokeDash(spec.mark)
  ) {
    localEncoding.strokeDash = {
      field: seriesField,
      type: categoricalEncoding.type,
      scale: {
        range: CONFIDENCE_LINE_DASH_PATTERNS.map(pattern => [...pattern]),
      },
    };
    spec.encoding = localEncoding;
  }

  const strokeDashEncoding = localEncoding.strokeDash ?? encoding.strokeDash;
  if (
    typeof strokeDashEncoding === 'object' &&
    strokeDashEncoding !== null &&
    !Array.isArray(strokeDashEncoding) &&
    (strokeDashEncoding as Record<string, unknown>).field === seriesField
  ) {
    const lineLegend = getStrokeLegend(categoricalEncoding.legend);
    categoricalEncoding.legend = lineLegend;
    (strokeDashEncoding as Record<string, unknown>).legend = lineLegend;
  }
};

const injectSharedLayerScales = (
  spec: Record<string, unknown>,
  palettes: ConfidenceSeriesPalettes,
  occupiedColors: Set<string>,
  inheritedEncoding: Record<string, unknown>,
  rows: Record<string, unknown>[],
  repeatValues: RepeatValues,
  layerRowsBySpec: ReadonlyMap<
    Record<string, unknown>,
    Record<string, unknown>[]
  >,
  datasets: Record<string, unknown> | undefined,
  transformRuntime: ConfidenceSeriesTransformRuntime | undefined,
  inheritedSharedChannels: ReadonlyMap<string, number | undefined>,
): Map<string, number | undefined> => {
  if (!Array.isArray(spec.layer)) return new Map();

  const resolveScale =
    isRecord(spec.resolve) && isRecord(spec.resolve.scale)
      ? spec.resolve.scale
      : {};
  const sharedChannels = new Map<string, number | undefined>();

  for (const channel of CONFIDENCE_PRESENTATION_CHANNELS) {
    if (
      resolveScale[channel] === 'independent' ||
      inheritedSharedChannels.has(channel)
    )
      continue;

    const collectEntries = (
      layer: Record<string, unknown>,
      parentEncoding: Record<string, unknown>,
      parentRows: Record<string, unknown>[],
      parentDatasets: Record<string, unknown> | undefined,
    ): {
      encoding: Record<string, unknown>;
      isSeries: boolean;
      values: readonly unknown[] | undefined;
    }[] => {
      const encoding = { ...parentEncoding, ...getEncoding(layer) };
      const layerDatasets = getDatasets(layer, parentDatasets);
      const layerRows =
        layerRowsBySpec.get(layer) ??
        getTransformedRows(layer, parentRows, layerDatasets, transformRuntime);
      if (getMarkType(layer.mark) === undefined) {
        const independent =
          isRecord(layer.resolve) &&
          isRecord(layer.resolve.scale) &&
          layer.resolve.scale[channel] === 'independent';
        if (independent || !Array.isArray(layer.layer)) return [];
        return layer.layer.flatMap(child =>
          isRecord(child)
            ? collectEntries(child, encoding, layerRows, layerDatasets)
            : [],
        );
      }
      const categoricalEncoding = encoding[channel];
      if (
        !isRecord(categoricalEncoding) ||
        !isCategoricalEncoding(categoricalEncoding) ||
        'condition' in categoricalEncoding ||
        categoricalEncoding.scale === null
      )
        return [];
      return [
        {
          encoding: categoricalEncoding,
          isSeries: isThemeableSeries(layer, encoding),
          values: getCategoricalEncodingValues(
            categoricalEncoding,
            layerRows,
            repeatValues,
          ),
        },
      ];
    };
    const entries = spec.layer.flatMap(layer =>
      isRecord(layer)
        ? collectEntries(layer, inheritedEncoding, rows, datasets)
        : [],
    );

    if (entries.length < 2 || !entries.some(entry => entry.isSeries)) continue;
    sharedChannels.set(channel, undefined);
    const hasUnknownValues = entries.some(entry => entry.values === undefined);
    const replacesStoredPalette = entries.some(entry =>
      hasSeriesPalette(entry.encoding),
    );
    if (
      !replacesStoredPalette &&
      (hasUnknownValues ||
        entries.some(entry => hasAuthoredScale(entry.encoding)))
    ) {
      for (const entry of entries)
        rememberSeriesColors(entry.encoding, palettes, occupiedColors);
      continue;
    }

    const distinctValues = new Set(
      entries
        .flatMap(entry => entry.values ?? [])
        .map(getDistinctValueKey)
        .filter(value => value !== null && value !== undefined),
    );
    const encodingType = entries.every(
      entry => entry.encoding.type === 'ordinal',
    )
      ? 'ordinal'
      : 'nominal';
    const scale = hasUnknownValues
      ? { scheme: CONFIDENCE_OVERFLOW_CATEGORICAL_SCHEME }
      : getConfidenceSeriesScale(distinctValues.size, encodingType, palettes);
    if (scale === undefined) continue;
    sharedChannels.set(
      channel,
      hasUnknownValues ? undefined : distinctValues.size,
    );

    for (const encoding of new Set(entries.map(entry => entry.encoding))) {
      setSeriesScale(encoding, scale);
      rememberSeriesColors(
        encoding,
        palettes,
        occupiedColors,
        hasUnknownValues ? undefined : distinctValues.size,
      );
    }
  }

  return sharedChannels;
};

const injectConfidenceSeriesStylesInSpec = (
  spec: Record<string, unknown>,
  palettes: ConfidenceSeriesPalettes,
  occupiedColors: Set<string>,
  inheritedEncoding: Record<string, unknown> = {},
  inheritedRows: Record<string, unknown>[] = [],
  inheritedDatasets: Record<string, unknown> | undefined = undefined,
  transformRuntime: ConfidenceSeriesTransformRuntime | undefined = undefined,
  inheritedRepeatValues: RepeatValues = {},
  inheritedSharedChannels: ReadonlyMap<string, number | undefined> = new Map(),
  precomputedRows: Record<string, unknown>[] | undefined = undefined,
): void => {
  const datasets = getDatasets(spec, inheritedDatasets);
  const rows =
    precomputedRows ??
    getTransformedRows(spec, inheritedRows, datasets, transformRuntime);
  const localEncoding = getEncoding(spec) ?? {};
  const encoding = { ...inheritedEncoding, ...localEncoding };
  const markType = getMarkType(spec.mark);
  const repeatValues = getRepeatValues(spec.repeat, inheritedRepeatValues);
  const layerRowsBySpec = new Map<
    Record<string, unknown>,
    Record<string, unknown>[]
  >();
  if (Array.isArray(spec.layer)) {
    for (const layer of spec.layer) {
      if (typeof layer !== 'object' || layer === null || Array.isArray(layer)) {
        continue;
      }
      const layerSpec = layer as Record<string, unknown>;
      layerRowsBySpec.set(
        layerSpec,
        getTransformedRows(
          layerSpec,
          rows,
          getDatasets(layerSpec, datasets),
          transformRuntime,
        ),
      );
    }
  }
  const layerSharedChannels = injectSharedLayerScales(
    spec,
    palettes,
    occupiedColors,
    encoding,
    rows,
    repeatValues,
    layerRowsBySpec,
    datasets,
    transformRuntime,
    inheritedSharedChannels,
  );
  const sharedChannels = new Map([
    ...inheritedSharedChannels,
    ...layerSharedChannels,
  ]);
  if (isRecord(spec.resolve) && isRecord(spec.resolve.scale)) {
    for (const channel of CONFIDENCE_PRESENTATION_CHANNELS) {
      if (spec.resolve.scale[channel] === 'independent')
        sharedChannels.delete(channel);
    }
  }

  if (markType === 'bar') {
    removeRedundantBarValueColor(localEncoding, encoding);
    normalizeCategoricalBarValueScale(encoding);
  }

  if (isThemeableSeries(spec, encoding)) {
    for (const channel of CONFIDENCE_PRESENTATION_CHANNELS) {
      const encodedField = encoding[channel];
      if (typeof encodedField !== 'object' || encodedField === null) continue;

      const categoricalEncoding = encodedField as Record<string, unknown>;
      if (
        !isCategoricalEncoding(categoricalEncoding) ||
        'condition' in categoricalEncoding
      )
        continue;
      if (sharedChannels.has(channel)) {
        const distinctValueCount = sharedChannels.get(channel);
        if (distinctValueCount !== undefined) {
          injectLineDashStyle(
            spec,
            localEncoding,
            encoding,
            categoricalEncoding,
            distinctValueCount,
            palettes,
          );
        }
        continue;
      }
      if (hasAuthoredScale(categoricalEncoding)) {
        rememberSeriesColors(categoricalEncoding, palettes, occupiedColors);
        continue;
      }
      const values = getCategoricalEncodingValues(
        categoricalEncoding,
        rows,
        repeatValues,
      );
      if (values === undefined && !hasSeriesPalette(categoricalEncoding)) {
        rememberSeriesColors(categoricalEncoding, palettes, occupiedColors);
        continue;
      }
      const distinctValueCount =
        values === undefined
          ? undefined
          : new Set(
              values
                .map(getDistinctValueKey)
                .filter(value => value !== null && value !== undefined),
            ).size;
      const scale =
        distinctValueCount === undefined
          ? { scheme: CONFIDENCE_OVERFLOW_CATEGORICAL_SCHEME }
          : getConfidenceSeriesScale(
              distinctValueCount,
              categoricalEncoding.type,
              palettes,
            );
      if (scale === undefined) continue;

      setSeriesScale(categoricalEncoding, scale);
      rememberSeriesColors(
        categoricalEncoding,
        palettes,
        occupiedColors,
        distinctValueCount,
      );
      if (distinctValueCount !== undefined) {
        injectLineDashStyle(
          spec,
          localEncoding,
          encoding,
          categoricalEncoding,
          distinctValueCount,
          palettes,
        );
      }
    }
  }

  if (
    markType === 'bar' &&
    !hasExplicitMarkPresentation(spec.mark) &&
    !CONFIDENCE_PRESENTATION_CHANNELS.some(
      channel => encoding[channel] !== undefined,
    )
  ) {
    const categoryPosition = getCategoricalPosition(encoding);
    if (categoryPosition !== undefined) {
      const distinctValueCount = getDistinctValueCount(
        rows,
        categoryPosition.field,
      );
      if (distinctValueCount >= 2) {
        const seriesScale = getConfidenceSeriesScale(
          distinctValueCount,
          categoryPosition.type,
          palettes,
        );
        localEncoding.color = {
          field: categoryPosition.field,
          type: categoryPosition.type,
          ...(seriesScale !== undefined ? { scale: seriesScale } : {}),
          legend: null,
        };
        rememberSeriesColors(
          localEncoding.color as Record<string, unknown>,
          palettes,
          occupiedColors,
          distinctValueCount,
        );
      }
    }
  }

  for (const child of getChildSpecs(spec)) {
    injectConfidenceSeriesStylesInSpec(
      child,
      palettes,
      occupiedColors,
      encoding,
      rows,
      datasets,
      transformRuntime,
      repeatValues,
      sharedChannels,
      layerRowsBySpec.get(child),
    );
  }
};

export const injectConfidenceSeriesStyles = (
  spec: Record<string, unknown>,
  palettes: ConfidenceSeriesPalettes,
  transformRuntime?: ConfidenceSeriesTransformRuntime,
): void => {
  const config = normalizeCategoricalConfig(spec, palettes);
  prepareSeriesPresentation(spec, config);
  const usage: SeriesColorUsage = {
    literals: new Set(),
    configuredDefaults: new Map(),
    occupied: new Set(),
  };
  injectConfidenceSeriesStylesInSpec(
    spec,
    palettes,
    usage.occupied,
    {},
    [],
    undefined,
    transformRuntime,
  );
  collectSeriesColorUsage(spec, usage, config, palettes.sequential[0]);
  const explicitColors = [
    ...new Set([
      ...[...usage.configuredDefaults.values()].filter(
        (color): color is string =>
          color !== undefined && usage.literals.has(color),
      ),
      ...usage.literals,
    ]),
  ];
  let seriesColors = palettes.categorical;
  if (usage.occupied.size > 0) {
    const availableColors = [
      ...new Set([...palettes.categorical, ...palettes.overflow]),
    ].filter(color => !usage.occupied.has(color));
    seriesColors =
      availableColors.length > 0 ? availableColors : palettes.overflow;
  } else if (explicitColors.length === 1)
    seriesColors = palettes.sequential.slice(0, 1);
  else if (explicitColors.length > palettes.categorical.length)
    seriesColors = palettes.overflow;
  const colorMapping = new Map(
    explicitColors.map<[string, string]>((color, index) => [
      color,
      seriesColors[index % seriesColors.length],
    ]),
  );
  for (const [markType, identity] of usage.configuredDefaults) {
    config[markType] = normalizeSeriesMarkConfig(
      config,
      markType,
      identity === undefined
        ? palettes.sequential[0]
        : (colorMapping.get(identity) ?? palettes.sequential[0]),
    );
  }
  applyThemeToLegacySeriesColors(spec, colorMapping);
};

const hasDateOnlyValues = (
  field: unknown,
  rows: Record<string, unknown>[],
): boolean =>
  typeof field === 'string' &&
  rows.length > 0 &&
  rows.every(row =>
    typeof row[field] === 'string'
      ? DATE_ONLY_VALUE.test(row[field] as string)
      : false,
  );

const injectUtcScale = (temporalEncoding: Record<string, unknown>): void => {
  const scale = temporalEncoding.scale;
  if (scale === undefined) {
    temporalEncoding.scale = { type: 'utc' };
  } else if (
    typeof scale === 'object' &&
    scale !== null &&
    !Array.isArray(scale) &&
    !('type' in scale)
  ) {
    temporalEncoding.scale = {
      ...(scale as Record<string, unknown>),
      type: 'utc',
    };
  }
};

const normalizeDateOnlyTemporalAxesInSpec = (
  spec: Record<string, unknown>,
  inheritedRows: Record<string, unknown>[] = [],
): void => {
  const rows = getInlineDataRows(spec, inheritedRows);
  if (spec.encoding && typeof spec.encoding === 'object') {
    const encoding = spec.encoding as Record<string, unknown>;
    for (const channel of ['x', 'y']) {
      const encodedField = encoding[channel];
      if (typeof encodedField !== 'object' || encodedField === null) continue;

      const temporalEncoding = encodedField as Record<string, unknown>;
      const axis = temporalEncoding.axis;
      if (
        temporalEncoding.type !== 'temporal' ||
        typeof axis !== 'object' ||
        axis === null
      ) {
        continue;
      }

      const temporalAxis = axis as Record<string, unknown>;
      if (!hasDateOnlyFormat(temporalAxis.format)) continue;

      if (hasDateOnlyValues(temporalEncoding.field, rows)) {
        injectUtcScale(temporalEncoding);
      }

      const hasExplicitTickConfiguration = TEMPORAL_TICK_PROPERTIES.some(
        property => property in temporalAxis,
      );
      if (!hasExplicitTickConfiguration) {
        temporalAxis.tickMinStep = DAY_IN_MILLISECONDS;
      }
    }
  }

  for (const key of NESTED_SPEC_KEYS) {
    const child = spec[key];
    if (Array.isArray(child)) {
      for (const subSpec of child) {
        if (typeof subSpec === 'object' && subSpec !== null) {
          normalizeDateOnlyTemporalAxesInSpec(
            subSpec as Record<string, unknown>,
            rows,
          );
        }
      }
    } else if (typeof child === 'object' && child !== null) {
      normalizeDateOnlyTemporalAxesInSpec(
        child as Record<string, unknown>,
        rows,
      );
    }
  }
};

export function normalizeDateOnlyTemporalAxes(
  spec: Record<string, unknown>,
): void {
  normalizeDateOnlyTemporalAxesInSpec(spec);
}

const ISO_DATE_OR_ZONED_TIMESTAMP =
  /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2}))?$/;

const asDefinition = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;

const usesTemporalScale = (definition: Record<string, unknown>): boolean => {
  if (definition.scale === null || definition.timeUnit !== undefined)
    return false;
  const scaleType = asDefinition(definition.scale)?.type;
  return scaleType === undefined
    ? definition.type === 'temporal'
    : scaleType === 'time' || scaleType === 'utc';
};

const normalizeTemporalDatumsInSpec = (
  spec: Record<string, unknown>,
  inheritedEncoding: Record<string, unknown> = {},
): void => {
  const localEncoding = asDefinition(spec.encoding) ?? {};
  const encoding = { ...inheritedEncoding, ...localEncoding };

  if (spec.mark !== undefined) {
    for (const channel of ['x', 'y', 'x2', 'y2']) {
      const definition = asDefinition(encoding[channel]);
      const primaryDefinition = asDefinition(encoding[channel[0]]);
      if (
        !definition ||
        !primaryDefinition ||
        !usesTemporalScale(primaryDefinition) ||
        typeof definition.datum !== 'string' ||
        !ISO_DATE_OR_ZONED_TIMESTAMP.test(definition.datum)
      ) {
        continue;
      }

      const timestamp = Date.parse(definition.datum);
      const date = definition.datum.slice(0, 10);
      const calendarDate = Date.parse(date);
      if (
        !Number.isFinite(timestamp) ||
        !Number.isFinite(calendarDate) ||
        new Date(calendarDate).toISOString().slice(0, 10) !== date
      ) {
        continue;
      }
      localEncoding[channel] = { ...definition, datum: timestamp };
      spec.encoding = localEncoding;
    }
  }

  for (const key of NESTED_SPEC_KEYS) {
    const children = Array.isArray(spec[key]) ? spec[key] : [spec[key]];
    for (const child of children) {
      const childSpec = asDefinition(child);
      if (childSpec) {
        normalizeTemporalDatumsInSpec(
          childSpec,
          key === 'layer' ? encoding : {},
        );
      }
    }
  }
};

export const normalizeTemporalDatums = (
  spec: Record<string, unknown>,
): void => {
  if (isVegaLiteSpec(spec)) normalizeTemporalDatumsInSpec(spec);
};

const pathMarkTypes = ['line', 'area'];

const enableMarkTooltip = (spec: Record<string, unknown>): void => {
  const markValue = spec.mark;
  let mark: Record<string, unknown> | undefined;
  if (typeof markValue === 'string') {
    mark = { type: markValue };
  } else if (typeof markValue === 'object' && markValue !== null) {
    mark = markValue as Record<string, unknown>;
  }

  if (!mark) return;

  if (mark.tooltip === false) {
    spec.mark = mark;
    return;
  }

  const markType = String(mark.type);
  if (!pathMarkTypes.includes(markType)) {
    mark.tooltip ??= true;
    spec.mark = mark;
    return;
  }

  mark.tooltip = false;
  mark.point = { opacity: 0, size: 400, tooltip: true };
  spec.mark = mark;
};

const removeVisiblePointLayers = (spec: Record<string, unknown>): void => {
  if (!Array.isArray(spec.layer)) return;

  const hasPathLayer = spec.layer.some(
    layer =>
      typeof layer === 'object' &&
      layer !== null &&
      pathMarkTypes.includes(
        getMarkType((layer as Record<string, unknown>).mark) ?? '',
      ),
  );
  if (!hasPathLayer) return;

  spec.layer = spec.layer.filter(layer => {
    if (typeof layer !== 'object' || layer === null) return true;
    const markType = getMarkType((layer as Record<string, unknown>).mark);
    return markType !== 'point' && markType !== 'circle';
  });
};

const injectTooltipInteractivityInSpec = (
  spec: Record<string, unknown>,
): void => {
  removeVisiblePointLayers(spec);
  enableMarkTooltip(spec);

  for (const key of NESTED_SPEC_KEYS) {
    const child = spec[key];
    if (Array.isArray(child)) {
      for (const childSpec of child) {
        if (typeof childSpec === 'object' && childSpec !== null) {
          injectTooltipInteractivityInSpec(
            childSpec as Record<string, unknown>,
          );
        }
      }
    } else if (typeof child === 'object' && child !== null) {
      injectTooltipInteractivityInSpec(child as Record<string, unknown>);
    }
  }
};

export const injectTooltipInteractivity = (
  spec: Record<string, unknown>,
): void => {
  if (!isVegaLiteSpec(spec)) return;

  injectTooltipInteractivityInSpec(spec);
};
